import { CODE_SYNTAX, CODE_SYNTAX_ATTR, MonoMeta, Pill, StatGrid } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';

import { ChatSurface } from './ChatSurface';

import type { ReactElement } from 'react';

/**
 * The three steps, in order. Lives here rather than in `HowItWorks` because
 * both halves of that band need it — the step list and the stage — and the
 * list is its own component now. A constant in the parent would have meant the
 * child importing the parent, which is the cycle `.claude/rules/architecture.md`
 * treats as evidence the abstraction is wrong.
 */
export const STEP_IDS = ['install', 'learn', 'work'] as const;

/**
 * The three panels of the how-it-works stage: the install snippet, what the
 * agent reads, and the live conversation.
 *
 * Hoisted out of `HowItWorks` as a leaf — they import nothing from it, and the
 * parent is then purely the step-selection shell. Each panel is exactly a third
 * of the sliding track's width, which is why `Panel` owns that number rather
 * than each caller repeating it.
 */

/* ------------------------------------------------------------------ */

/** One panel of the sliding strip. Each is exactly a third of the track. */
export function Panel({ children }: { readonly children: ReactElement | readonly ReactElement[] }) {
  return (
    <div className="flex w-[33.3333%] flex-col justify-center gap-4 px-8 py-7">{children}</div>
  );
}

export function StepInstall(): ReactElement {
  const { t } = useT();
  return (
    <Panel>
      <div className="flex flex-wrap items-baseline gap-3">
        <h3 className="text-xl font-semibold tracking-[-0.032em] text-[color:var(--bd-ink)]">
          {t('home.how.install.stageTitle')}
        </h3>
        <MonoMeta>{t('home.how.install.time')}</MonoMeta>
      </div>

      {/* The snippet is the product's real embed, so it is never translated
          and never abbreviated — a visitor should be able to read it and know
          exactly what they are pasting. */}
      <div
        className="relative overflow-hidden rounded-[13px] bg-[color:var(--bd-ink)] px-5 py-4 font-mono text-[13px] leading-relaxed"
        style={{ color: CODE_SYNTAX.plain }}
      >
        <span
          aria-hidden="true"
          data-bd-anim
          className="absolute inset-y-0 w-2/5"
          style={{
            background: 'linear-gradient(90deg,transparent,rgba(43,98,255,.16),transparent)',
            animation: 'bdSheen 4.5s cubic-bezier(.16,1,.3,1) infinite',
          }}
        />
        <code dir="ltr" className="relative block">
          <span style={{ color: CODE_SYNTAX.punctuation }}>&lt;script</span>{' '}
          <span style={{ color: CODE_SYNTAX_ATTR }}>src</span>=
          <span style={{ color: CODE_SYNTAX.string }}>&quot;cdn.syncanix.com/w.js&quot;</span>
          <br />
          &nbsp;&nbsp;<span style={{ color: CODE_SYNTAX_ATTR }}>data-key</span>=
          <span style={{ color: CODE_SYNTAX.string }}>&quot;pk_live_…&quot;</span>
          <span style={{ color: CODE_SYNTAX.punctuation }}>&gt;&lt;/script&gt;</span>
        </code>
      </div>

      <div className="flex flex-col gap-2.5">
        <MonoMeta className="!tracking-[0.13em] rtl:!tracking-normal">
          {t('home.how.install.whereLabel')}
        </MonoMeta>
        <div className="flex flex-wrap gap-2">
          <Pill>{t('home.how.install.where1')}</Pill>
          <Pill>{t('home.how.install.where2')}</Pill>
          <Pill>{t('home.how.install.where3')}</Pill>
        </div>
      </div>

      <p className="text-pretty text-sm leading-[1.62] text-[color:var(--bd-muted)]">
        {t('home.how.install.note')}
      </p>
    </Panel>
  );
}

export function StepLearn(): ReactElement {
  const { t } = useT();
  const sources = [
    { id: 's1', dot: 'var(--bd-cyan)', tagTone: 'ok' as const },
    { id: 's2', dot: 'var(--bd-blue)', tagTone: 'info' as const },
    { id: 's3', dot: 'var(--bd-violet)', tagTone: 'plain' as const },
  ];
  const TAG: Record<'ok' | 'info' | 'plain', string> = {
    ok: 'bg-[rgba(52,211,153,.11)] text-[#0f766e]',
    info: 'bg-[rgba(43,98,255,.08)] text-[#2348db]',
    plain: 'bg-[#f1f1f4] text-[color:var(--bd-subtle)]',
  };

  return (
    <Panel>
      <div className="flex flex-wrap items-baseline gap-3">
        <h3 className="text-xl font-semibold tracking-[-0.032em] text-[color:var(--bd-ink)]">
          {t('home.how.learn.stageTitle')}
        </h3>
        <MonoMeta>{t('home.how.learn.time')}</MonoMeta>
      </div>

      <ul className="flex list-none flex-col gap-2 p-0">
        {sources.map((source) => (
          <li
            key={source.id}
            className="flex items-start gap-3 rounded-xl border border-[color:var(--bd-line-2)] bg-[color:var(--bd-sunk)] px-3.5 py-3"
          >
            <span
              aria-hidden="true"
              className="mt-1.5 size-[7px] flex-none rounded-full"
              style={{ background: source.dot }}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-[13.5px] font-semibold tracking-[-0.02em] text-[color:var(--bd-ink)]">
                {t(`home.how.learn.${source.id}.title`)}
              </span>
              <span className="mt-1 block text-[12.5px] leading-snug text-[color:var(--bd-subtle)]">
                {t(`home.how.learn.${source.id}.body`)}
              </span>
            </span>
            <span
              className={[
                'flex-none whitespace-nowrap rounded-full px-2 py-1 font-mono rtl:font-sans text-[10px] uppercase tracking-[0.08em] rtl:normal-case rtl:tracking-normal',
                TAG[source.tagTone],
              ].join(' ')}
            >
              {t(`home.how.learn.${source.id}.tag`)}
            </span>
          </li>
        ))}
      </ul>

      {/* Illustrative figures from one representative scan, not a claim about
          every customer — the label says "a typical first pass". */}
      <StatGrid
        stats={[
          { value: '214', label: t('home.how.learn.stat1') },
          { value: '38', label: t('home.how.learn.stat2'), accent: true },
          { value: '26', label: t('home.how.learn.stat3') },
        ]}
      />

      <p className="text-pretty text-[13.5px] leading-[1.6] text-[color:var(--bd-muted)]">
        {t('home.how.learn.note')}
      </p>
    </Panel>
  );
}

export function StepWork({ active }: { readonly active: boolean }): ReactElement {
  const { t } = useT();
  return (
    <Panel>
      <div className="flex flex-wrap items-baseline gap-3">
        <h3 className="text-xl font-semibold tracking-[-0.032em] text-[color:var(--bd-ink)]">
          {t('home.how.work.stageTitle')}
        </h3>
        <MonoMeta>{t('home.how.work.time')}</MonoMeta>
      </div>
      <ChatSurface active={active} />
      <p className="text-pretty text-[13px] leading-[1.55] text-[color:var(--bd-muted)]">
        {t('home.how.work.note')}
      </p>
    </Panel>
  );
}
